// Ficha de una regla en Comisiones pagadas: lo pagado en el periodo, su costo por semana y quién
// la activó esta semana.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionCountByEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionCountByEnum';
import { CommissionPayTimingEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionPayTimingEnum';
import { CopyableId } from '@/kit';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { StatusPill } from '@/shared/patterns/Pills';
import { useLivePayout } from '../../api/commissionQueries';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import {
    contributorsOf,
    sellersAtLeastTitle,
    sellersChip,
    type RulePaidRow,
} from '../../lib/paidRows';
import { sharePct } from '../../lib/payoutRows';
import {
    describeCondition,
    describeLevers,
    describeScope,
    PAY_TIMING_LABEL,
    STATUS_LABEL,
    STATUS_TONE,
} from '../../lib/ruleText';
import { SECTION_TITLE } from '../fieldStyles';
import { ContributorsList } from './ContributorsList';
import { SeriesSection } from './SeriesSection';

const CHIP = 'rounded-full border border-border px-2 py-0.5 text-[10.5px]';

interface RulePaidDetailProps {
    row: RulePaidRow;
    rule: RetailCommission.CommissionRule | undefined;
    totalCents: number;
    when: string;
    directory: CommissionDirectory;
    /** Qué tanto cuadra la lista de quién aportó con la fila. */
    contributorsNote: string | null;
}

export function RulePaidDetail({
    row,
    rule,
    totalCents,
    when,
    directory,
    contributorsNote,
}: RulePaidDetailProps) {
    const live = useLivePayout();
    const liveReady = live.data !== undefined && !live.isError;
    const contributors = contributorsOf(live.data?.sellers ?? [], 'RULE', row.ruleId);
    const currentCents = liveReady
        ? contributors.reduce((sum, contributor) => sum + contributor.paidCents, 0)
        : null;

    return (
        <div className="flex h-full min-h-0 flex-col overflow-auto">
            <header className="flex flex-col gap-1 border-b border-border px-4 py-3">
                <div className="flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
                    <span className="flex-none">{row.kind ?? 'Regla'} ·</span>
                    <CopyableId inline label="ID" value={row.ruleId} className="min-w-0" />
                </div>
                <h2 className="truncate text-[15px] font-semibold">{row.name}</h2>
                <p className="text-[24px] font-semibold tabular-nums">
                    {formatMoneyCents(row.paidCents)}
                </p>
                <ul className="flex flex-wrap items-center gap-1" aria-label="Resumen de la regla">
                    <li className={CHIP} title={sellersAtLeastTitle(row)}>
                        {sellersChip(row)}
                    </li>
                    <li className={CHIP}>
                        {sharePct(row.paidCents, totalCents).toFixed(1)}% del total · {when}
                    </li>
                    {row.status && (
                        <li>
                            <StatusPill tone={STATUS_TONE[row.status]}>
                                {STATUS_LABEL[row.status]}
                            </StatusPill>
                        </li>
                    )}
                </ul>
            </header>
            <section className="border-b border-border px-4 py-3 text-[11px]">
                {rule ? (
                    <dl className="grid grid-cols-[80px_1fr] gap-x-2 gap-y-1">
                        <dt className="text-muted-foreground">Palanca</dt>
                        <dd>{describeLevers(rule.levers, rule.countBy)}</dd>
                        <dt className="text-muted-foreground">Condición</dt>
                        <dd>{describeCondition(rule.condition, rule.countBy)}</dd>
                        <dt className="text-muted-foreground">Alcance</dt>
                        <dd>{describeScope(rule.scope)}</dd>
                        <dt className="text-muted-foreground">Se paga</dt>
                        <dd>
                            {PAY_TIMING_LABEL[rule.payTiming ?? CommissionPayTimingEnum.ON_CUT]}
                            {rule.onSaleAdvanceBps != null &&
                                rule.payTiming !== CommissionPayTimingEnum.ON_SALE &&
                                ` · ${String(rule.onSaleAdvanceBps / 100)}% al momento`}
                        </dd>
                        {rule.description !== null && (
                            <>
                                <dt className="text-muted-foreground">Descripción</dt>
                                <dd>{rule.description}</dd>
                            </>
                        )}
                    </dl>
                ) : (
                    <p className="text-muted-foreground">La regla ya no está en el catálogo.</p>
                )}
                {row.kind === 'Multiplicador' && (
                    <p className="mt-2 text-muted-foreground">
                        Su efecto está repartido en las reglas que multiplica; por eso aquí suma $0.
                    </p>
                )}
            </section>
            <SeriesSection
                by="RULE"
                id={row.ruleId}
                currentCents={currentCents}
                currentPeriodId={live.data?.periodId}
                currentLoading={live.isLoading}
                title="Costo semanal"
            />
            <section className="px-4 py-3">
                <h3 className={SECTION_TITLE}>Top vendedores esta semana</h3>
                <ContributorsList
                    contributors={contributors}
                    directory={directory}
                    isLoading={live.isLoading}
                    isError={!live.isLoading && !liveReady}
                    note={contributorsNote}
                    limit={10}
                    unitLabel={rule?.countBy === CommissionCountByEnum.SALE ? 'ventas' : 'pzas'}
                    label="Top vendedores esta semana"
                    empty="Ningún vendedor activó esta regla esta semana."
                />
            </section>
        </div>
    );
}
